import { useCallback, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import {
  EligibleWorkflowsAssigneesDocument,
  WorkflowRoleSelectorDocument,
  WorkflowTeamSelectorDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { HandlerFormComponent } from 'modules/workflows/contexts/WorkflowContext'
import { getDurationUnitOptions } from 'modules/workflows/lib/durationUnit'

import { ApproverSelectionContainer } from './ApproverSelectionContainer'
import { BasicTaskHandlerSchema } from './schemas/basicTaskHandler'
import { StrategyItem } from './utils/interfaces'
import { strategiesFromPayload, strategiesToPayload } from './utils/strategyUtils'

export const BasicTaskHandlerForm = ({}: HandlerFormComponent) => {
  const { company } = useCurrentSession()
  const { control, watch, setValue, getValues } = useFormContext<BasicTaskHandlerSchema>()

  const { loading: employeesLoading, data: employeesData } = useQuery(
    EligibleWorkflowsAssigneesDocument,
    {
      variables: {
        companyId: company.id,
        active: true,
      },
    }
  )
  const { loading: teamsLoading, data: teamsData } = useQuery(WorkflowTeamSelectorDocument)

  const { data: rolesData, loading: rolesLoading } = useQuery(WorkflowRoleSelectorDocument, {
    variables: { companyId: company.id },
  })

  const employees = employeesData?.procurement?.eligibleWorkflowsAssignees ?? []
  const teams = teamsData?.teams?.teamsConnection?.nodes || []
  const legacyRoles = rolesData?.jobCatalog?.rolesConnection?.nodes || []
  const treeRoles = rolesData?.jobCatalog?.treeNodesConnection?.nodes || []

  const payload = watch('nodeData.payload')

  const { strategies, fallbackStrategies } = useMemo(() => {
    if (!payload) {
      return {
        strategies: [],
        fallbackStrategies: [],
      }
    }
    return strategiesFromPayload<'strategies' | 'fallbackStrategies'>([
      {
        type: 'ids',
        data: payload.assignee_ids,
        groupAs: 'strategies',
      },
      {
        type: 'ids',
        data: payload.fallback_assignee_ids,
        groupAs: 'fallbackStrategies',
      },
      {
        type: 'segments',
        data: payload.assignee_segments,
        groupAs: 'strategies',
      },
      {
        type: 'segments',
        data: payload.fallback_assignee_segments,
        groupAs: 'fallbackStrategies',
      },
    ])
  }, [payload])

  const handleMutateStrategies = useCallback(
    (data: { add?: StrategyItem[]; fallbackAdd?: StrategyItem[]; remove?: string }) => {
      if (!data.add?.length && !data.fallbackAdd?.length && !data.remove) {
        return
      }

      const { add = [], fallbackAdd = [], remove } = data

      const allStrategies = [...strategies, ...add].filter((strategy) => remove !== strategy.id)
      const allFallbackStrategies = [...fallbackStrategies, ...fallbackAdd].filter(
        (strategy) => remove !== strategy.id
      )

      const currentPayload = getValues().nodeData.payload

      const newPayload = strategiesToPayload<typeof payload>(
        [
          {
            field: 'assignee_ids',
            type: 'ids',
            data: allStrategies,
          },
          {
            field: 'assignee_segments',
            type: 'segments',
            data: allStrategies,
          },
          {
            field: 'fallback_assignee_ids',
            type: 'ids',
            data: allFallbackStrategies,
          },
          {
            field: 'fallback_assignee_segments',
            type: 'segments',
            data: allFallbackStrategies,
          },
        ],
        currentPayload
      )

      setValue('nodeData.payload', newPayload, { shouldValidate: true, shouldDirty: true })
    },
    [strategies, fallbackStrategies, getValues, setValue]
  )

  const unselectableEmployees = useMemo(
    () => [payload?.assignee_ids, payload?.fallback_assignee_ids].flat().filter(Boolean),
    [payload?.assignee_ids, payload?.fallback_assignee_ids]
  )

  const unselectableTeams = useMemo(
    () =>
      [payload?.assignee_segments, payload?.fallback_assignee_segments]
        .flat()
        .filter((segment) => segment && ['anyone_in_team', 'team_leads'].includes(segment.name))
        .map(
          (segment) => segment && ('team_id' in segment.input ? segment.input.team_id : undefined)
        )
        .filter(Boolean),
    [payload?.assignee_segments, payload?.fallback_assignee_segments]
  )

  const unselectableRoles = useMemo(
    () =>
      [payload?.assignee_segments, payload?.fallback_assignee_segments]
        .flat()
        .filter((segment) => segment && segment.name === 'job_catalog_role')
        .map((segment) =>
          segment && 'role_id' in segment.input ? segment.input.role_id : undefined
        )
        .filter(Boolean),
    [payload?.assignee_segments, payload?.fallback_assignee_segments]
  )

  const unselectableStrategies = useMemo(
    () =>
      [strategies, fallbackStrategies]
        .flat()
        .filter((strategy) =>
          ['employee_manager', 'admins', 'resource_owner'].includes(strategy.type)
        )
        .map((strategy) => strategy.type),
    [strategies, fallbackStrategies]
  )

  return (
    <Box gap="s16">
      {/* Task Title */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.title_label')}
        </Text>
        <Controller
          name="nodeData.payload.title"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              size="md"
              label={i18n.t('workflows.sidepanel.task.title_label')}
              hideLabel
              placeholder={i18n.t('workflows.sidepanel.task.title_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Task Description */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.description_label')}
        </Text>
        <Controller
          name="nodeData.payload.description"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              size="md"
              label={i18n.t('workflows.sidepanel.task.description_label')}
              hideLabel
              placeholder={i18n.t('workflows.sidepanel.task.description_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Assignees */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.assignees_label')}
        </Text>
        <ApproverSelectionContainer
          employees={employees}
          unselectableEmployees={unselectableEmployees}
          legacyRoles={legacyRoles}
          treeRoles={treeRoles}
          unselectableRoles={unselectableRoles}
          teams={teams}
          unselectableTeams={unselectableTeams}
          strategies={strategies}
          unselectableStrategies={unselectableStrategies}
          entityName={i18n.t('workflows.sidepanel.approval.entities.assignee')}
          isLoading={employeesLoading || teamsLoading || rolesLoading}
          onAddStrategies={(data) => handleMutateStrategies({ add: data })}
          onDeleteStrategy={(id) => handleMutateStrategies({ remove: id })}
        />
      </Box>
      {
        /* Fallback Assignees */
        <Box gap="s8" width="full">
          <Text size="200" weight="medium" className="text-f1-foreground-secondary">
            {i18n.t('workflows.sidepanel.approval.fields.fallback_assignee')}
          </Text>
          <ApproverSelectionContainer
            employees={employees}
            unselectableEmployees={unselectableEmployees}
            legacyRoles={legacyRoles}
            treeRoles={treeRoles}
            unselectableRoles={unselectableRoles}
            teams={teams}
            unselectableTeams={unselectableTeams}
            strategies={fallbackStrategies}
            unselectableStrategies={unselectableStrategies}
            entityName={i18n.t('workflows.sidepanel.approval.entities.fallback_assignee')}
            isLoading={employeesLoading || teamsLoading || rolesLoading}
            maxStrategies={1}
            onAddStrategies={(data) => handleMutateStrategies({ fallbackAdd: data })}
            onDeleteStrategy={(id) => handleMutateStrategies({ remove: id })}
          />
        </Box>
      }

      {/* Due On */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.due_on_label')}
        </Text>
        <Box flexDirection="row" gap="s8" width="full" alignItems="baseline">
          <Box style={{ width: '100px' }}>
            <Controller
              name="nodeData.payload.due_on_value"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  size="md"
                  label={i18n.t('workflows.sidepanel.task.due_on_label')}
                  hideLabel
                  placeholder="2"
                  value={String(field.value)}
                  onChange={(value) => field.onChange(Number(value))}
                  error={fieldState.error?.message}
                  type="number"
                />
              )}
            />
          </Box>
          <Box width="full">
            <Controller
              name="nodeData.payload.due_on_unit"
              control={control}
              render={({ field, fieldState }) => (
                <F0Select
                  size="md"
                  label={i18n.t('workflows.sidepanel.task.due_on_label')}
                  hideLabel
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  options={getDurationUnitOptions()}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
